import React, { useState } from 'react';
import { Link } from 'gatsby';
import { nanoid } from 'nanoid';

import { LayoutCom } from 'components/Layout';
import { ContainerBackground, ContainerDefault } from 'components/Containers';
import { Image } from 'components/Image';
import { SEO } from 'components/SEO';
import { SolutionBlockItem } from 'components/SolutionBlockItem';
import { HomePageTab } from 'components/HomePageTab';
import { useGetImageNodeByPath, sendEventToGTM, getContentBlockById, getBlockItemsById } from 'helpers';
import RightArrow from 'icons/arrow-right.svg';
import { HomePageUseCases } from 'components/HomePageUseCases';
import { Accordion } from 'components/Accordion/Accordion';
import { BookButton } from 'components/Button';

import { HomepageContent, CtaBook } from '../content/homepage/HomepageContent';
import { MetaTagsHomepage } from '../content/homepage/meta/MetaData';

import 'swiper/components/scrollbar/scrollbar.scss';

const Talenteer = () => {
  enum HomePageBlockIds {
    Header = 'header',
    ProvenSolution = 'proven-solution',
    MainFeatures = 'main-features',
    CtaEnsure = 'cta-ensure',
    FindOut = 'find-out',
    Challenges = 'challenges',
    Blogs = 'blogs',
    Faq = 'faq',
  }

  const bookDemoText = 'Hello! I would like to book a demo of Talenteer.';
  const becomePartnerText = 'Hello! I would like to find out more about Talenteer partnership program.';

  const [contactFormDefaultText, setContactFormDefaultText] = useState(bookDemoText);

  const onMainCtaButtonClick = (type: 'book' | 'partner') => {
    setContactFormDefaultText(type === 'book' ? bookDemoText : becomePartnerText);
    sendEventToGTM('cta_click', { type: type === 'book' ? 'button_demo' : 'button_partner' });
  };

  return (
    <LayoutCom TextareaDefaultValue={contactFormDefaultText} layoutClass="no-overflow homepage">
      <div id="side-menu-scroll-container" key="homepage">
        <ContainerDefault containerClass="talent-flow">
          <h1 className="h0-title">{getContentBlockById(HomePageBlockIds.Header, HomepageContent).fieldTitle}</h1>
          <div className="body-1">{getContentBlockById(HomePageBlockIds.Header, HomepageContent).fieldDescription}</div>
          <div className="book-buttons">
            <BookButton type="book" onClickHandler={onMainCtaButtonClick} buttonText="Book a demo" />
            <BookButton
              type="partner"
              onClickHandler={onMainCtaButtonClick}
              buttonText="Become a partner"
              buttonClass="book-partner"
            />
          </div>
          <div className="nice-picture desktop-tablet-only">
            <Image
              alt={getBlockItemsById(HomePageBlockIds.Header, HomepageContent)[0].fieldImage?.alt || ''}
              imagePath={getBlockItemsById(HomePageBlockIds.Header, HomepageContent)[0].fieldImage?.imagePath}
            />
          </div>
        </ContainerDefault>
        <ContainerBackground containerClass="proven-solution has-no-margin-bottom">
          <h2 className="h1-title">
            {getContentBlockById(HomePageBlockIds.ProvenSolution, HomepageContent).fieldTitle}
          </h2>
          <div className="solutions">
            <div className="solution-row row-1">
              {getBlockItemsById(HomePageBlockIds.ProvenSolution, HomepageContent)
                .slice(0, 4)
                .map((item, index) => (
                  <SolutionBlockItem
                    key={nanoid()}
                    title={item.fieldTitle}
                    description={item.fieldDescription ?? ''}
                    showIcon={index === 3}
                    className={`row-1-item-${index + 1}`}
                  />
                ))}
            </div>
            <div className="solution-row row-2">
              {getBlockItemsById(HomePageBlockIds.ProvenSolution, HomepageContent)
                .slice(4)
                .map((item, index) => (
                  <SolutionBlockItem
                    key={nanoid()}
                    title={item.fieldTitle}
                    description={item.fieldDescription ?? ''}
                    className={`row-2-item-${index + 1}`}
                  />
                ))}
            </div>
          </div>
        </ContainerBackground>
        <ContainerBackground containerClass="main-feature has-no-margin-bottom">
          <h2 className="h1-title">{getContentBlockById(HomePageBlockIds.MainFeatures, HomepageContent).fieldTitle}</h2>
          <HomePageTab
            tabsClass="main-features-tabs"
            tabsItem={getBlockItemsById(HomePageBlockIds.MainFeatures, HomepageContent).map((item) => ({
              title: item.fieldTitle,
              rows:
                item.faq?.map((row) => ({
                  subTitle: row.question,
                  description: row.answer,
                })) ?? [],
            }))}
          />
        </ContainerBackground>
        <ContainerBackground containerClass="find-out has-no-margin-bottom">
          <h2 className="h1-title">{getContentBlockById(HomePageBlockIds.FindOut, HomepageContent).fieldTitle}</h2>

          <HomePageUseCases
            useCases={
              getBlockItemsById(HomePageBlockIds.FindOut, HomepageContent).map((item) => ({
                title: item.fieldTitle ?? '',
                link: item.fieldLink ?? '',
                description: item.fieldDescription ?? '',
              })) ?? []
            }
          />
        </ContainerBackground>
        <ContainerDefault containerClass="challenges">
          <h2 className="h1-title">{getContentBlockById(HomePageBlockIds.Challenges, HomepageContent).fieldTitle}</h2>

          <div className="block">
            {getBlockItemsById(HomePageBlockIds.Challenges, HomepageContent).map((item) => (
              <div className="block-item" key={nanoid()}>
                <h3 className="h3-title">{item.fieldTitle}</h3>
                <p className="body-3">{item.fieldDescription}</p>
              </div>
            ))}
          </div>
        </ContainerDefault>

        <ContainerBackground containerClass="cta-ensure has-no-margin-bottom">
          <h2 className="h1-title">{getContentBlockById(HomePageBlockIds.CtaEnsure, HomepageContent).fieldTitle}</h2>
          <BookButton
            type="book"
            onClickHandler={onMainCtaButtonClick}
            buttonText="Book a demo"
            buttonClass="book-partner"
          />
        </ContainerBackground>

        <ContainerBackground containerClass="blogs">
          <h2 className="h1-title">{getContentBlockById(HomePageBlockIds.Blogs, HomepageContent).fieldTitle}</h2>
          <Link className="view-all button-font" to="/blog">
            View all articles
          </Link>

          <div className="block">
            {getBlockItemsById(HomePageBlockIds.Blogs, HomepageContent).map((item) => (
              <div className="block-item" key={nanoid()}>
                <Image imagePath={item.fieldImage?.imagePath} alt={item.fieldImage?.alt ?? ''} className="cover" />
                <div className="content">
                  <div>
                    <h3 className="h3-title">{item.fieldTitle}</h3>
                    <p className="body-3">{item.fieldDescription}</p>
                  </div>
                  <Link to={item.fieldLink ?? '/'} className="read-article button-font">
                    <span className="button-font">Read article</span> <RightArrow />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </ContainerBackground>
        <ContainerDefault containerClass="faq">
          <h2 className="h1-title">{getContentBlockById(HomePageBlockIds.Faq, HomepageContent).fieldTitle}</h2>

          <div className="two-columns">
            <div className="faq-block">
              <Accordion
                items={
                  getBlockItemsById(HomePageBlockIds.Faq, HomepageContent).map((item) => ({
                    title: item.fieldTitle ?? '',
                    content: <p className="body-3">{item.fieldDescription}</p>,
                  })) ?? []
                }
              />
            </div>
            <div className="cta-block">
              <span>
                <h3 className="h2-title">{CtaBook.fieldTitle}</h3>
                <p className="body-2">{CtaBook.fieldSecondaryDescription}</p>
              </span>
              <BookButton
                type="book"
                onClickHandler={onMainCtaButtonClick}
                buttonText={CtaBook.fieldDescription}
                buttonClass="book-partner"
              />
            </div>
          </div>
        </ContainerDefault>
      </div>
    </LayoutCom>
  );
};

export default Talenteer;

export const Head = () => {
  const relatedImagePathUrl = useGetImageNodeByPath('meta/talenteer-by-itransition.webp').publicURL;

  return <SEO relatedImagePath={relatedImagePathUrl} metaTags={MetaTagsHomepage} />;
};
